<template>
  <section v-editable="blok" class="hero-animated" :style="{
    ...(desktopImg ? { '--hero-bg-image': `url(${desktopImg})` } : {}),
    ...(mobileImg ? { '--hero-bg-image-mobile': `url(${mobileImg})` } : {}),
  }">
    <div class="container hero-animated__container">
      <div class="hero-animated__content">
        <StoryblokComponent v-for="block in blok.content" :key="block._uid" :blok="block" />
      </div>
    </div>
    <div ref="feedEl" class="chat-feed" aria-hidden="true">
      <div ref="trackEl" class="chat-track" />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap'
import type { SbHeroHomepageAnimatedStoryblok } from '@/types'
import sygnetSrc from '~/assets/icons/sygnet.svg?url'

interface Props {
  blok: SbHeroHomepageAnimatedStoryblok
}

const props = defineProps<Props>()
const { t } = useI18n()
const feedEl = ref<HTMLElement | null>(null)
const trackEl = ref<HTMLElement | null>(null)

const desktopImg = computed(() => props.blok.bg_image?.filename ?? null)
const mobileImg = computed(() => props.blok.bg_image_mobile?.filename ?? null)

useHead({
  link: [
    ...(desktopImg.value ? [{ rel: 'preload', as: 'image' as const, href: desktopImg.value, media: '(min-width: 768px)' }] : []),
    ...(mobileImg.value ? [{ rel: 'preload', as: 'image' as const, href: mobileImg.value, media: '(max-width: 767px)' }] : []),
    ...(!mobileImg.value && desktopImg.value ? [{ rel: 'preload', as: 'image' as const, href: desktopImg.value, media: '(max-width: 767px)' }] : []),
  ],
})

const TS = 1.25
const SHIFT_DUR = 0.46 * TS
const MORPH_DUR = 0.62 * TS
const EASE = 'power2.out'
const REST_PX = 48
const END_HOLD = 2.6 * TS
const GLASS_BLUR_MAX = 60

let running = false
let rafId: number | null = null
const tweens: gsap.core.Tween[] = []

function killAll() {
  running = false
  tweens.forEach(t => t.kill())
  tweens.length = 0
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

function wait(seconds: number): Promise<void> {
  return new Promise(resolve => {
    if (!running) { resolve(); return }
    const dc = gsap.delayedCall(seconds, resolve)
    tweens.push(dc as unknown as gsap.core.Tween)
  })
}

function el(cls: string, html?: string): HTMLElement {
  const d = document.createElement('div')
  d.className = cls
  if (html != null) d.innerHTML = html
  return d
}

function icon(): HTMLElement {
  const ic = el('chat-icon')
  const img = document.createElement('img')
  img.src = sygnetSrc
  img.alt = ''
  ic.appendChild(img)
  return ic
}

function animateIn(item: HTMLElement, node: HTMLElement) {
  if (!trackEl.value) return
  const h = item.offsetHeight
  const t1 = gsap.fromTo(trackEl.value, { y: h }, { y: 0, duration: SHIFT_DUR, ease: EASE })
  const t2 = gsap.fromTo(node, { opacity: 0, y: REST_PX - h }, { opacity: 1, y: 0, duration: SHIFT_DUR, ease: EASE })
  tweens.push(t1, t2)
}

function appendItem(node: HTMLElement, right = false): HTMLElement {
  if (!trackEl.value) return node
  const item = el('chat-item' + (right ? ' chat-item--right' : ''))
  item.appendChild(node)
  trackEl.value.appendChild(item)
  return item
}

function addItem(node: HTMLElement, right = false): HTMLElement {
  const item = appendItem(node, right)
  animateIn(item, node)
  return item
}

function addAgent(text: string) {
  if (!running) return
  const bub = el('chat-bubble chat-bubble--agent')
  bub.appendChild(icon())
  const col = el('chat-col')
  col.appendChild(el('chat-txt', text))
  bub.appendChild(col)
  addItem(bub, false)
}

function addUser(text: string) {
  if (!running) return
  const bub = el('chat-bubble chat-bubble--user')
  const col = el('chat-col')
  col.appendChild(el('chat-txt', text))
  bub.appendChild(col)
  addItem(bub, true)
}

function addLabel(text: string) {
  if (!running || !trackEl.value) return
  const box = el(
    'chat-label',
    `${text}<span class="chat-label__dots"><span>.</span><span>.</span><span>.</span></span>`,
  )
  const item = el('chat-item chat-item--label')
  item.appendChild(box)
  trackEl.value.appendChild(item)

  const h = item.offsetHeight
  const t1 = gsap.fromTo(trackEl.value, { y: h }, { y: 0, duration: SHIFT_DUR, ease: EASE })
  const t2 = gsap.fromTo(box, { opacity: 0, y: REST_PX - h }, { opacity: 1, y: 0, duration: SHIFT_DUR, ease: EASE })
  tweens.push(t1, t2)

  const dots = box.querySelectorAll<HTMLElement>('.chat-label__dots span')
  dots.forEach((dot, i) => {
    const td = gsap.fromTo(dot, { opacity: 0 }, {
      opacity: 1,
      duration: 0.22 * TS,
      ease: EASE,
      delay: SHIFT_DUR + 0.26 * TS * (i + 1),
    })
    tweens.push(td)
  })
}

async function morph03(chipText: string, questionText: string): Promise<void> {
  if (!running || !trackEl.value) return
  return new Promise<void>(resolve => {
    const bub = el('chat-bubble chat-bubble--agent')
    bub.appendChild(icon())
    const col = el('chat-col')
    const q = el('chat-txt', questionText)
    col.appendChild(q)
    bub.appendChild(col)
    const item = appendItem(bub, false)

    col.style.width = col.offsetWidth + 'px'
    const qH = col.offsetHeight
    col.style.position = 'relative'
    col.style.overflow = 'hidden'

    q.style.position = 'absolute'
    q.style.top = '0'
    q.style.left = '0'
    q.style.right = '0'
    q.style.opacity = '0'

    const chip = el('chat-muted', chipText)
    chip.style.position = 'absolute'
    chip.style.top = '0'
    chip.style.left = '0'
    chip.style.right = '0'
    col.appendChild(chip)

    const chipH = chip.offsetHeight
    col.style.height = (chipH + 3) + 'px'

    animateIn(item, bub)

    const dc = gsap.delayedCall(1.2 * TS, () => {
      if (!running) { resolve(); return }
      const tl = gsap.timeline({
        onComplete: () => {
          q.style.position = ''
          q.style.top = ''
          q.style.left = ''
          q.style.right = ''
          chip.style.display = 'none'
          col.style.height = 'auto'
          col.style.overflow = 'visible'
          resolve()
        },
      })
      tl.to(col, { height: qH, duration: MORPH_DUR, ease: EASE }, 0)
      tl.to(chip, { opacity: 0, y: -7, duration: MORPH_DUR * 0.42, ease: EASE }, 0)
      tl.fromTo(q, { opacity: 0, y: 9 }, { opacity: 1, y: 0, duration: MORPH_DUR, ease: EASE }, MORPH_DUR * 0.52)
    })
    tweens.push(dc as unknown as gsap.core.Tween)
  })
}

async function morph10(titleText: string, bodyText: string): Promise<void> {
  if (!running || !trackEl.value) return
  return new Promise<void>(resolve => {
    const bub = el('chat-bubble chat-bubble--agent')
    bub.appendChild(icon())
    const col = el('chat-col')
    const title = el('chat-title', titleText)
    const body = el('chat-txt', bodyText)
    col.appendChild(title)
    col.appendChild(body)
    bub.appendChild(col)
    appendItem(bub, false)

    // Lock width before measuring to get stable offsetHeight
    col.style.width = col.offsetWidth + 'px'
    const fullH = col.offsetHeight
    const titleH = title.offsetHeight

    col.style.overflow = 'hidden'
    body.style.opacity = '0'
    if (titleH > 0) col.style.height = titleH + 'px'

    animateIn(bub.parentElement as HTMLElement, bub)

    const dc = gsap.delayedCall(1.2 * TS, () => {
      if (!running) { resolve(); return }
      const tl = gsap.timeline({
        onComplete: () => {
          col.style.height = 'auto'
          col.style.overflow = 'visible'
          resolve()
        },
      })
      if (titleH > 0 && fullH > titleH) {
        tl.to(col, { height: fullH, duration: MORPH_DUR, ease: EASE }, 0)
      }
      tl.to(body, { opacity: 1, duration: MORPH_DUR, ease: EASE }, 0)
    })
    tweens.push(dc as unknown as gsap.core.Tween)
  })
}

async function addBooking(
  titleText: string,
  subtitleText: string,
  dayText: string,
  slots: string[],
): Promise<void> {
  if (!running || !trackEl.value) return

  const bub = el('chat-bubble chat-bubble--agent')
  bub.appendChild(icon())
  const col = el('chat-col')
  col.appendChild(el('chat-title', titleText))
  col.appendChild(el('chat-subtle', subtitleText))

  const panel = el('chat-panel')
  panel.appendChild(el('chat-day', dayText))
  const slotsEl = el('chat-slots')
  slots.forEach(s => slotsEl.appendChild(el('chat-slot', s)))
  panel.appendChild(slotsEl)
  col.appendChild(panel)
  bub.appendChild(col)

  const item = appendItem(bub, false)
  const h = Math.max(item.offsetHeight, 1)

  gsap.set(bub, { opacity: 0, y: 30 })

  const t1 = gsap.fromTo(trackEl.value!, { y: h }, { y: 0, duration: SHIFT_DUR, ease: EASE })
  tweens.push(t1)

  await wait(SHIFT_DUR + 0.04)
  if (!running) return

  const t2 = gsap.to(bub, { opacity: 1, y: 0, duration: SHIFT_DUR * 1.5, ease: EASE })
  tweens.push(t2)

  await wait(SHIFT_DUR * 1.5 + 1.2 * TS)
  if (!running) return

  const firstSlot = slotsEl.querySelector<HTMLElement>('.chat-slot')
  if (firstSlot) firstSlot.classList.add('chat-slot--active')
}

function startTickFades() {
  const LERP = 0.05
  const OPACITY_STEPS = [1, 0.88, 0.65, 0.38, 0]
  const opacityState = new WeakMap<HTMLElement, number>()

  const tick = () => {
    if (feedEl.value && trackEl.value) {
      const children = Array.from(trackEl.value.children)
      const lastIdx = children.length - 1
      children.forEach((it, idx) => {
        const itemEl = it as HTMLElement
        const dist = lastIdx - idx
        const targetF = OPACITY_STEPS[Math.min(dist, OPACITY_STEPS.length - 1)] ?? 0

        const current = opacityState.get(itemEl) ?? 1
        const smoothed = current + (targetF - current) * LERP
        opacityState.set(itemEl, smoothed)
        itemEl.style.opacity = String(smoothed)

        const bub = itemEl.querySelector<HTMLElement>('.chat-bubble')
        if (bub) {
          const s = bub.style as CSSStyleDeclaration & { webkitBackdropFilter: string }
          if (dist === 0) {
            bub.style.backdropFilter = ''
            s.webkitBackdropFilter = ''
          } else {
            const blurVal = smoothed >= 0.999 ? '' : `blur(${(smoothed * smoothed * GLASS_BLUR_MAX).toFixed(1)}px) saturate(1.15)`
            bub.style.backdropFilter = blurVal
            s.webkitBackdropFilter = blurVal
          }
        }
      })
    }
    rafId = requestAnimationFrame(tick)
  }
  rafId = requestAnimationFrame(tick)
}

async function run() {
  if (!trackEl.value) return
  running = true
  trackEl.value.innerHTML = ''
  tweens.length = 0

  addAgent(t('common.hero_animated.chat_agent_1'))
  await wait(1.3 * TS)

  addUser(t('common.hero_animated.chat_user_1'))
  await wait(1.7 * TS)

  await morph03(
    t('common.hero_animated.chat_symptoms_chip'),
    t('common.hero_animated.chat_safety_check'),
  )
  await wait(2.0 * TS)

  addUser(t('common.hero_animated.chat_user_2'))
  await wait(1.5 * TS)

  addAgent(t('common.hero_animated.chat_agent_2'))
  await wait(1.5 * TS)

  addUser(t('common.hero_animated.chat_user_3'))
  await wait(1.4 * TS)

  addLabel(t('common.hero_animated.chat_label'))
  await wait(1.5 * TS)

  addAgent(t('common.hero_animated.chat_agent_3'))
  await wait(1.4 * TS)

  addUser(t('common.hero_animated.chat_user_4'))
  await wait(1.4 * TS)

  await morph10(
    t('common.hero_animated.chat_consult_title'),
    t('common.hero_animated.chat_consult_body'),
  )
  await wait(MORPH_DUR + 3.6 * TS)

  await addBooking(
    t('common.hero_animated.chat_booking_title'),
    t('common.hero_animated.chat_booking_subtitle'),
    t('common.hero_animated.chat_booking_day'),
    ['8:00', '8:15', '8:30', '9:30', '10:00'],
  )
  await wait(3.0 * TS)

  await wait(END_HOLD)

  if (running) run()
}

onMounted(() => {
  startTickFades()

  const observer = new IntersectionObserver(
    entries => {
      if (entries[0]?.isIntersecting && !running) run()
    },
    { threshold: 0.1 },
  )
  if (feedEl.value) observer.observe(feedEl.value)
})

onUnmounted(() => {
  killAll()
})
</script>

<style lang="scss" scoped>
// ── Layout ────────────────────────────────────────────────────────────────────

.hero-animated {
  background-image: var(--hero-bg-image-mobile, var(--hero-bg-image, none));
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;

  @media (min-width: $breakpoint-md) {
    background-image: var(--hero-bg-image, none);
    background-position: 70% 30%;
  }

  @media (min-width: $breakpoint-lg) {
    background-position: center;
  }

  display: flex;
  flex-direction: column;

  @media (min-width: $breakpoint-lg) {
    display: block;
    position: relative;
    overflow: hidden;
    min-height: 636px;
  }

  &__container {
    position: relative;
    z-index: 1;
    padding-top: 128px;
    padding-bottom: 24px;
    display: flex;
    align-items: center;

    @media (min-width: $breakpoint-md) {
      padding-top: 128x;
      padding-bottom: 64px;
    }

    @media (min-width: $breakpoint-lg) {
      padding-top: 200px;
      padding-bottom: 128px;
      min-height: 636px;
    }
  }

  &__content {
    width: 100%;

    @media (min-width: $breakpoint-lg) {
      max-width: 53%;
    }

    :deep(h1) {
      @include heading-3;

      @media (min-width: $breakpoint-md) {
        @include heading-1;
      }
    }
  }
}

// ── Chat feed ─────────────────────────────────────────────────────────────────

.chat-feed {
  position: relative;
  display: flex;
  width: 329px;
  height: 260px;
  margin: 24px auto 0 16px;
  padding-bottom: 24px;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  z-index: 2;

  @media (min-width: $breakpoint-md) {
    height: 320px;
    margin: -48px 80px 0 auto;
    padding-bottom: 40px;
  }

  @media (min-width: $breakpoint-lg) {
    position: absolute;
    right: 15%;
    width: 27%;
    bottom: 0;
    height: 64%;
    padding-bottom: 48px;
    margin: 0;
    max-height: 360px;
  }
}

.chat-track {
  display: flex;
  flex-direction: column;
  will-change: transform;
}

// ── Chat items ────────────────────────────────────────────────────────────────

:deep(.chat-item) {
  flex: 0 0 auto;
  padding-top: 12px;
  display: flex;

  &:first-child {
    padding-top: 0;
  }
}

:deep(.chat-item--right) {
  justify-content: flex-end;
}

:deep(.chat-item--label) {
  padding-top: 22px;
  justify-content: center;
}

:deep(.chat-item--label + .chat-item) {
  padding-top: 22px;
}

// ── Bubbles ───────────────────────────────────────────────────────────────────

:deep(.chat-bubble) {
  position: relative;
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  gap: 12px;
  max-width: 350px;
  background: rgba(140, 140, 140, 0.30);
  border-radius: 12px;
  -webkit-backdrop-filter: blur(60px) saturate(1.15);
  backdrop-filter: blur(60px) saturate(1.15);
  box-shadow: inset 0.5px 0.5px 0.5px rgba(255, 255, 255, 0.16);
  padding: 10px 14px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    pointer-events: none;
    background: linear-gradient(150deg,
        rgba(255, 255, 255, 0.55) 0%,
        rgba(255, 255, 255, 0.18) 45%,
        rgba(255, 255, 255, 0) 100%);
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
}

:deep(.chat-bubble--user) {
  padding: 10px 14px;
  max-width: 300px;
}

:deep(.chat-icon) {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  display: flex;
  align-items: center;
  padding: 2.5px;
  border-radius: 127px;
  background: rgba(255, 255, 255, 0.20);
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    position: relative;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    pointer-events: none;
    z-index: 2;
    background: linear-gradient(150deg,
        rgba(255, 255, 255, 0.55) 0%,
        rgba(255, 255, 255, 0.18) 45%,
        rgba(255, 255, 255, 0) 100%);
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
}

:deep(.chat-col) {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;
}

// ── Typography ────────────────────────────────────────────────────────────────

:deep(.chat-txt) {
  color: rgba(255, 255, 255, 0.97);
  font-size: 12px;
  line-height: 22px;
  font-weight: 500;
  letter-spacing: 0.1px;
}

:deep(.chat-muted) {
  color: rgba(255, 255, 255, 0.62);
  font-size: 11px;
  font-style: italic;
  line-height: 1.3;
  margin-top: 3px;
}

:deep(.chat-title) {
  color: rgba(255, 255, 255, 0.97);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.25;
}

:deep(.chat-subtle) {
  color: rgba(255, 255, 255, 0.62);
  font-size: 11px;
  font-weight: 400;
}

// ── Booking panel ─────────────────────────────────────────────────────────────

:deep(.chat-panel) {
  margin-top: 5px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 13px;
  padding: 11px;
}

:deep(.chat-day) {
  color: rgba(255, 255, 255, 0.97);
  font-size: 12px;
  margin-bottom: 8px;
}

:deep(.chat-slots) {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

:deep(.chat-slot) {
  flex: 0 0 auto;
  padding: 6px 10px;
  border-radius: 9px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.62);
  background: rgba(255, 255, 255, 0.1);
  transition:
    background 0.6s ease,
    color 0.6s ease;
}

:deep(.chat-slot--active) {
  background: #4a44e0;
  color: #fff;
}

// ── Label separator ───────────────────────────────────────────────────────────

:deep(.chat-label) {
  font-style: italic;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.78);
  font-size: 10px;
  letter-spacing: 0.2px;
  padding: 3px 0;
  white-space: nowrap;
}

:deep(.chat-label__dots) {
  span {
    opacity: 0;
  }
}
</style>
